import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { AdminSidebar } from '@/components/AdminSidebar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Play, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Download, 
  RefreshCw,
  Users,
  MapPin,
  AlertTriangle,
  FileText,
  Zap,
  Search,
  FileCheck,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { mockInterview, mockResearch } from '@/lib/mock-data';
import { validatePlanQuality, PlanQualityReport } from '@/lib/plan-validator';

type StepStatus = 'pending' | 'running' | 'success' | 'error';

interface PipelineStep {
  id: string;
  name: string;
  status: StepStatus;
  duration?: number;
  result?: any;
  error?: string;
}

export default function AdminTestPipeline() {
  const navigate = useNavigate();
  const { user, isSuperAdmin, loading: authLoading } = useAuth();
  
  const [steps, setSteps] = useState<PipelineStep[]>([
    { id: 'interview', name: 'Load Mock Interview', status: 'pending' },
    { id: 'research', name: 'Research Swarm (8 Agents)', status: 'pending' },
    { id: 'compile', name: 'Deep Compilation', status: 'pending' },
    { id: 'pdf', name: 'PDF Generation', status: 'pending' },
    { id: 'validate', name: 'Quality Validation', status: 'pending' },
  ]);
  
  const [isRunning, setIsRunning] = useState(false);
  const [interviewId, setInterviewId] = useState<string | null>(null);
  const [planId, setPlanId] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [qualityReport, setQualityReport] = useState<PlanQualityReport | null>(null);
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, `[${timestamp}] ${message}`]);
  };

  const updateStep = (id: string, updates: Partial<PipelineStep>) => {
    setSteps(prev => prev.map(step => 
      step.id === id ? { ...step, ...updates } : step
    ));
  };

  const runFullPipeline = async () => {
    if (!user) {
      toast.error('Please log in first');
      return;
    }

    setIsRunning(true);
    setLogs([]);
    setQualityReport(null);
    setPdfUrl(null);
    
    // Reset all steps
    setSteps(prev => prev.map(step => ({ ...step, status: 'pending' as StepStatus, duration: undefined, error: undefined })));

    try {
      // Step 1: Create mock interview
      addLog('Starting pipeline with mock interview data...');
      updateStep('interview', { status: 'running' });
      const startInterview = Date.now();
      
      const { data: interview, error: interviewError } = await supabase
        .from('interviews')
        .insert({
          user_id: user.id,
          name: mockInterview.name,
          email: mockInterview.email,
          location: mockInterview.location,
          household_size: mockInterview.household_size,
          household_ages: mockInterview.household_ages,
          household_members: mockInterview.household_members,
          pets: mockInterview.pets,
          property_type: mockInterview.property_type,
          property_ownership: mockInterview.property_ownership,
          property_details: mockInterview.property_details,
          emergency_types: mockInterview.emergency_types,
          medical_needs: mockInterview.medical_needs,
          special_needs: mockInterview.special_needs,
          budget: mockInterview.budget,
          ai_insights: mockInterview.ai_insights,
          nuanced_details: mockInterview.nuanced_details,
          phase_completed: mockInterview.phase_completed,
          status: 'completed',
        })
        .select()
        .single();

      if (interviewError) throw interviewError;
      
      setInterviewId(interview.id);
      updateStep('interview', { 
        status: 'success', 
        duration: Date.now() - startInterview,
        result: { id: interview.id, name: interview.name }
      });
      addLog(`✓ Created mock interview: ${interview.id}`);

      // Step 2: Run Research Swarm
      addLog('Executing research swarm with 8 agents...');
      updateStep('research', { status: 'running' });
      const startResearch = Date.now();

      const { data: researchData, error: researchError } = await supabase.functions.invoke('research-swarm', {
        body: {
          interviewId: interview.id,
          location: mockInterview.location,
          emergencyTypes: mockInterview.emergency_types,
          householdSize: mockInterview.household_size,
        },
      });

      if (researchError) {
        addLog(`⚠ Research swarm error: ${researchError.message}. Using mock data.`);
        // Continue with mock data if research fails
      }

      updateStep('research', { 
        status: researchError ? 'error' : 'success', 
        duration: Date.now() - startResearch,
        result: researchData || mockResearch,
        error: researchError?.message
      });
      addLog(`✓ Research completed in ${Date.now() - startResearch}ms`);

      // Step 3: Deep Compilation
      addLog('Running deep compilation with multi-pass generation...');
      updateStep('compile', { status: 'running' });
      const startCompile = Date.now();

      const { data: compiledPlan, error: compileError } = await supabase.functions.invoke('compile-report-deep', {
        body: {
          interviewId: interview.id,
          interview: {
            ...mockInterview,
            id: interview.id,
            user_id: user.id,
          },
          research: researchData || mockResearch,
        },
      });

      if (compileError) {
        updateStep('compile', { status: 'error', error: compileError.message });
        throw compileError;
      }

      updateStep('compile', { 
        status: 'success', 
        duration: Date.now() - startCompile,
        result: compiledPlan
      });
      addLog(`✓ Compilation completed: ${compiledPlan?.metadata?.estimated_pages || 'unknown'} pages estimated`);

      // Step 4: PDF Generation
      addLog('Generating PDF document...');
      updateStep('pdf', { status: 'running' });
      const startPdf = Date.now();

      // Store the compiled plan
      const { data: plan, error: planError } = await supabase
        .from('emergency_plans')
        .insert({
          user_id: user.id,
          interview_id: interview.id,
          plan_data: compiledPlan,
          pdf_metadata: {
            estimated_pages: compiledPlan?.metadata?.estimated_pages,
            readiness_score: compiledPlan?.metadata?.readiness_score,
            generated_at: new Date().toISOString(),
          },
          is_latest: true,
        })
        .select()
        .single();

      if (planError) throw planError;

      setPlanId(plan.id);
      updateStep('pdf', { 
        status: 'success', 
        duration: Date.now() - startPdf,
        result: { planId: plan.id }
      });
      addLog(`✓ Plan stored: ${plan.id}`);

      // Step 5: Quality Validation
      addLog('Running quality validation...');
      updateStep('validate', { status: 'running' });
      const startValidate = Date.now();

      const report = validatePlanQuality(compiledPlan, mockInterview);
      setQualityReport(report);

      updateStep('validate', { 
        status: report.passed ? 'success' : 'error', 
        duration: Date.now() - startValidate,
        result: report
      });
      
      addLog(`✓ Validation complete: ${report.passed ? 'PASSED' : 'FAILED'}`);
      addLog(`  - Pages: ${report.pageCount}/${report.pageCountTarget}`);
      addLog(`  - Emergency Types: ${report.emergencyTypesCovered}/${report.emergencyTypesRequired}`);
      addLog(`  - Overall Score: ${report.overallScore}%`);

      if (report.passed) {
        toast.success('Pipeline completed successfully!');
      } else {
        toast.warning('Pipeline completed with quality issues');
      }

    } catch (error: any) {
      console.error('Pipeline error:', error);
      addLog(`✗ Error: ${error.message}`);
      toast.error(`Pipeline failed: ${error.message}`);
    } finally {
      setIsRunning(false);
    }
  };

  const getStepIcon = (status: StepStatus) => {
    switch (status) {
      case 'pending': return <Clock className="h-5 w-5 text-muted-foreground" />;
      case 'running': return <Loader2 className="h-5 w-5 text-primary animate-spin" />;
      case 'success': return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'error': return <XCircle className="h-5 w-5 text-destructive" />;
    }
  };

  const getOverallProgress = () => {
    const completed = steps.filter(s => s.status === 'success' || s.status === 'error').length;
    return (completed / steps.length) * 100;
  };

  if (authLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>Super admin access required</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} className="w-full">
              Return Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar />
      
      <main className="flex-1 p-6 overflow-auto">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-3xl font-bold">Pipeline Test</h1>
            <p className="text-muted-foreground">
              End-to-end test of emergency plan generation with mock data
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column: Mock Data Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Mock Interview Data
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="font-medium">{mockInterview.name}</p>
                  <p className="text-sm text-muted-foreground">{mockInterview.email}</p>
                </div>
                
                <Separator />
                
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    {mockInterview.location.city}, {mockInterview.location.state}
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    {mockInterview.household_size} members (ages: {mockInterview.household_ages.join(', ')})
                  </span>
                </div>
                
                <Separator />
                
                <div>
                  <p className="text-sm font-medium mb-2">Emergency Types ({mockInterview.emergency_types.length})</p>
                  <div className="flex flex-wrap gap-1">
                    {mockInterview.emergency_types.map((type, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {type}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <Separator />
                
                <div>
                  <p className="text-sm font-medium mb-2">Special Needs</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {mockInterview.special_needs.map((need, idx) => (
                      <li key={idx}>• {need}</li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <p className="text-sm font-medium mb-2">Pets</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {mockInterview.pets.map((pet, idx) => (
                      <li key={idx}>• {pet.name} ({pet.type})</li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Middle Column: Pipeline Steps */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Zap className="h-5 w-5" />
                    Pipeline Execution
                  </CardTitle>
                  <Button 
                    onClick={runFullPipeline} 
                    disabled={isRunning}
                    size="lg"
                  >
                    {isRunning ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Running...
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4 mr-2" />
                        Run Full Pipeline
                      </>
                    )}
                  </Button>
                </div>
                <Progress value={getOverallProgress()} className="h-2" />
              </CardHeader>
              <CardContent className="space-y-4">
                {steps.map((step, idx) => (
                  <div key={step.id} className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      {getStepIcon(step.status)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-medium">{step.name}</p>
                        {step.duration && (
                          <span className="text-sm text-muted-foreground">
                            {step.duration}ms
                          </span>
                        )}
                      </div>
                      {step.error && (
                        <p className="text-sm text-destructive">{step.error}</p>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Quality Report */}
          {qualityReport && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileCheck className="h-5 w-5" />
                  Quality Validation Report
                  <Badge variant={qualityReport.passed ? 'default' : 'destructive'}>
                    {qualityReport.passed ? 'PASSED' : 'FAILED'}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <p className="text-2xl font-bold">{qualityReport.pageCount}</p>
                    <p className="text-sm text-muted-foreground">Pages (target: {qualityReport.pageCountTarget})</p>
                  </div>
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <p className="text-2xl font-bold">{qualityReport.emergencyTypesCovered}/{qualityReport.emergencyTypesRequired}</p>
                    <p className="text-sm text-muted-foreground">Emergency Types</p>
                  </div>
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <p className="text-2xl font-bold">{qualityReport.readinessScore}%</p>
                    <p className="text-sm text-muted-foreground">Readiness Score</p>
                  </div>
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <p className="text-2xl font-bold">{qualityReport.overallScore}%</p>
                    <p className="text-sm text-muted-foreground">Overall Score</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Sections */}
                  <div>
                    <h4 className="font-medium mb-3">Section Validation</h4>
                    <div className="space-y-2">
                      {qualityReport.sectionsComplete.map((section, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2">
                            {section.complete ? (
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                            ) : (
                              <XCircle className="h-4 w-4 text-destructive" />
                            )}
                            {section.section}
                          </span>
                          <span className="text-muted-foreground">{section.pageEstimate}p</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Issues & Recommendations */}
                  <div className="space-y-4">
                    {qualityReport.crossReferenceErrors.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2 text-destructive">Cross-Reference Errors</h4>
                        <ul className="text-sm space-y-1">
                          {qualityReport.crossReferenceErrors.map((error, idx) => (
                            <li key={idx} className="text-muted-foreground">• {error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {qualityReport.recommendations.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2">Recommendations</h4>
                        <ul className="text-sm space-y-1">
                          {qualityReport.recommendations.map((rec, idx) => (
                            <li key={idx} className="text-muted-foreground">• {rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Logs */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Search className="h-5 w-5" />
                Execution Logs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-64 w-full rounded-md border p-4">
                <div className="font-mono text-sm space-y-1">
                  {logs.length === 0 ? (
                    <p className="text-muted-foreground">No logs yet. Run the pipeline to see output.</p>
                  ) : (
                    logs.map((log, idx) => (
                      <p key={idx} className={log.includes('✗') ? 'text-destructive' : log.includes('⚠') ? 'text-yellow-600' : ''}>
                        {log}
                      </p>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Actions */}
          {planId && (
            <Card>
              <CardContent className="pt-6">
                <div className="flex gap-4">
                  <Button onClick={() => navigate(`/plan-generation/${interviewId}`)}>
                    <FileText className="h-4 w-4 mr-2" />
                    View Generated Plan
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/admin/reports')}>
                    View All Reports
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
